<template>
	<b-modal
		:id="id"
		header-bg-variant="primary"
		header-text-variant="light"
		:title="modalTitle"
		:ok-disabled="saving"
		:cancel-disabled="saving"
		:no-close-on-backdrop="saving"
		:no-close-on-esc="saving"
		:hide-header-close="saving"
		@ok="onSubmit"
		@show="onShow"
		@hidden="onHidden"
	>
		<b-form-group :label="$t('groups.client')">
			<b-badge>{{ client.name }}</b-badge>
		</b-form-group>
		<b-row>
			<b-col :cols="nameCols">
				<b-form-group
					:label="$t('groups.name')"
					label-for="name"
					:state="nameState"
					:invalid-feedback="nameError"
				>
					<b-form-input id="name" :autofocus="true" :state="nameState" v-model="name" />
					<b-form-text>Name must be at least 3 characters and can contain letters, numbers, dashes, underscores, spaces, commas, forward slashes and &</b-form-text>
				</b-form-group>
			</b-col>

			<b-col cols="2" v-if="!group">
				<b-form-group :label="$t('groups.active')">
					<b-form-checkbox switch size="lg" v-model="isActive" />
				</b-form-group>
			</b-col>
		</b-row>

		<b-form-group
			:label="$t('groups.parent')"
			label-for="parent"
			:description="$t('groups.parent_description')"
		>
			<b-form-select v-model="parent" :options="groups" :disabled="!parentEnabled" />
		</b-form-group>

		<template v-slot:modal-footer="{ ok, cancel }">
			<b-button variant="link" @click="cancel()">{{ $t('modal.cancel') }}</b-button>
			<b-button variant="primary" @click="ok()" :disabled="saving">
				{{ $t('modal.save') }}
				<b-spinner small v-if="saving" />
			</b-button>
		</template>
	</b-modal>
</template>

<script>
import {
	required,
	minLength,
	maxLength,
	helpers
} from 'vuelidate/lib/validators';

import { groupNameRegex } from '../../util/RegularExpressions';
import ErrorModal from '../../mixins/ErrorModal';

export default {
	props: {
		id: { default: 'add-group' },
		client: {
			required: true,
			type: Object
		},
		group: {
			default: null
		},
		clientGroups: {
			required: true,
			type: Array
		}
	},

	inject: [ 'groupRepository' ],

	mixins: [ ErrorModal ],

	data () {
		return {
			saving: false,
			submitted: false,
			name: '',
			parent: null,
			isActive: true
		};
	},

	validations: {
		name: {
			required,
			min: minLength(1),
			max: maxLength(100),
			regex: helpers.regex('groupName', groupNameRegex)
		}
	},

	methods: {
		onShow () {
			if (this.group) {
				this.name = this.group.name;
				this.isActive = this.group.is_active;
				this.parent = this.group.parent_group_id;
			}
		},

		onHidden () {
			this.saving = false;
			this.submitted = false;
			this.name = '';
			this.parent = null;
			this.isActive = true;

			this.$emit('hidden');
		},

		async onSubmit (bvEvent) {
			bvEvent.preventDefault();
			this.submitted = true;
			this.saving = true;

			if (this.$v.$invalid) {
				this.saving = false;
			}
			else {
				try {
					let group;
					if (this.group) {
						group = await this.groupRepository.update(this.group.id, this.client.id, this.name, this.parent, this.isActive);

						const sidebarGroup = {
							name: group.name,
							client_id: group.client_id,
							parent_group_id: group.parent_group_id,
							id: group.id
						};

						this.$store.commit('sidebar/UPDATE_GROUP', sidebarGroup);
					}
					else {
						group = await this.groupRepository.store(this.client.id, this.name, this.parent, this.isActive);

						const sidebarGroup = {
							name: group.name,
							client_id: group.client_id,
							parent_group_id: group.parent_group_id,
							id: group.id
						};

						this.$store.commit('sidebar/ADD_GROUP', sidebarGroup);
					}

					this.$bvModal.hide(this.id);
					this.$emit('submit', group);
				}
				catch (e) {
					this.showErrorModal(e, 'Error Saving Group');
				}

				this.saving = false;
				this.submitted = false;
			}
		},
		getBadGroups (group) {
			const id = parseInt(group.id);
			const idArray = [ id ];

			let resultArray = [].concat(idArray);

			const childGroups = this.clientGroups.filter(element => parseInt(element.parent_group_id) === id);

			childGroups.forEach(element => {
				resultArray = resultArray.concat(this.getBadGroups(element));
			});

			return resultArray;
		}
	},

	computed: {
		groups () {
			const noParent = { value: null, text: '' };

			const groupList = this.groupList.slice();

			const mappedGroups = groupList.map(group => (
				{
					value: group.id,
					text: group.name
				}
			));

			mappedGroups.unshift(noParent);

			return mappedGroups;
		},
		groupList () {
			if (!this.group) {
				return this.clientGroups;
			}

			const clientGroups = this.clientGroups;
			const badGroupIds = this.getBadGroups(this.group);

			return clientGroups.filter(element => {
				return !badGroupIds.includes(parseInt(element.id));
			});
		},

		parentEnabled () {
			return this.groups.length > 1;
		},

		nameError () {
			if (this.submitted) {
				if (!this.$v.name.required) {
					return this.$t('validation.required');
				}

				if (!this.$v.name.min) {
					return 'Group name must be at least 3 characters';
				}

				if (!this.$v.name.max) {
					return 'Group name can be a max of 50 characters';
				}

				if (!this.$v.name.regex) {
					return 'Group name does not match required conventions';
				}
			}

			return '';
		},

		nameState () {
			return (this.nameError === '') ? null : false;
		},

		modalTitle () {
			return this.group === null ? this.$t('groups.add_new') : this.$t('groups.edit');
		},
		nameCols () {
			if (!this.group) {
				return 10;
			}

			return 12;
		}
	},

	watch: {
		group () {
			if (this.group) {
				this.name = this.group.name;
				this.isActive = this.group.is_active;
				this.parent = this.group.parent_group_id;
			}
		}
	}
};
</script>
